import {
  PROJECT_SERVICE_IDS,
  normalizeProjectServices,
  type ProjectServiceId,
} from './projectServices';

const SERVICE_LABELS: Record<ProjectServiceId, string> = {
  openproject: 'OpenProject',
  gitea: 'Gitea repository',
  woodpecker: 'Woodpecker CI',
  nextcloud: 'Nextcloud files',
  collabra: 'Collabra documents',
};

export interface PlatformServicesChecklistProps {
  packageCatalog?: ReadonlyArray<{ id: string; label: string }>;
  availableServices: readonly string[];
  realmServices?: readonly string[];
  ecosystemServices?: readonly string[];
  selectedServices: readonly string[];
  onChange?: (services: ProjectServiceId[]) => void;
  readOnly?: boolean;
  canAddRealmServices?: boolean;
  canAddEcosystemServices?: boolean;
  idPrefix?: string;
  legend?: string;
}

export default function PlatformServicesChecklist({
  packageCatalog,
  availableServices,
  realmServices,
  ecosystemServices,
  selectedServices,
  onChange,
  readOnly = false,
  canAddRealmServices = false,
  canAddEcosystemServices = false,
  idPrefix = 'project-service',
  legend = 'Project services',
}: PlatformServicesChecklistProps) {
  const available = new Set(normalizeProjectServices(availableServices));
  const realmEnabled = new Set(
    normalizeProjectServices(realmServices ?? availableServices)
  );
  const ecosystemEnabled = new Set(
    normalizeProjectServices(ecosystemServices ?? availableServices)
  );
  const selected = new Set(normalizeProjectServices(selectedServices));
  const labels = new Map<ProjectServiceId, string>(
    PROJECT_SERVICE_IDS.map((id) => [id, SERVICE_LABELS[id]])
  );
  for (const entry of packageCatalog || []) {
    if ((PROJECT_SERVICE_IDS as readonly string[]).includes(entry.id)) {
      labels.set(entry.id as ProjectServiceId, entry.label);
    }
  }
  const catalogIds = normalizeProjectServices(
    packageCatalog?.map((entry) => entry.id) ?? PROJECT_SERVICE_IDS
  );
  const options = PROJECT_SERVICE_IDS.filter((service) => catalogIds.includes(service));

  const unavailableReason = (service: ProjectServiceId): string | null => {
    const label = labels.get(service) || service;
    if (!available.has(service)) {
      return `Ask a system admin about installing ${label}.`;
    }
    if (!realmEnabled.has(service) && !canAddRealmServices) {
      return `Ask your realm admin to make ${label} available.`;
    }
    if (!ecosystemEnabled.has(service) && !canAddEcosystemServices) {
      return `Ask your ecosystem admin to make ${label} available.`;
    }
    const dependency =
      service === 'woodpecker' ? 'gitea' : service === 'collabra' ? 'nextcloud' : null;
    if (dependency) {
      const dependencyReason = unavailableReason(dependency);
      if (dependencyReason) return dependencyReason;
    }
    if (readOnly) {
      return `Ask a project admin to change ${label} for this project.`;
    }
    return null;
  };

  const toggle = (service: ProjectServiceId, checked: boolean) => {
    if (readOnly || !onChange) return;
    const next = new Set(selected);
    if (checked) {
      next.add(service);
      // CI requires a repository; Collabra requires Nextcloud storage.
      if (service === 'woodpecker') next.add('gitea');
      if (service === 'collabra') next.add('nextcloud');
    } else {
      next.delete(service);
      if (service === 'gitea') next.delete('woodpecker');
      if (service === 'nextcloud') next.delete('collabra');
    }
    onChange(PROJECT_SERVICE_IDS.filter((candidate) => next.has(candidate) && available.has(candidate)));
  };

  return (
    <fieldset className="admin-orch-fieldset project-services-checklist">
      <legend>{legend}</legend>
      {options.map((service) => {
        const id = `${idPrefix}-${service}`;
        const label = labels.get(service) || service;
        const installed = available.has(service);
        const missingFromRealm = !realmEnabled.has(service);
        const missingFromEcosystem = !ecosystemEnabled.has(service);
        const reason = unavailableReason(service);
        const disabled = Boolean(reason);
        const status = !installed
          ? 'Not installed'
          : missingFromRealm
            ? canAddRealmServices
              ? 'Activate for realm'
              : 'Not available to realm'
            : missingFromEcosystem
              ? canAddEcosystemServices
                ? 'Activate for ecosystem'
                : 'Not available to ecosystem'
              : null;
        return (
          <label
            key={service}
            className={`admin-ecosystem-check${disabled ? ' is-disabled' : ''}`}
            htmlFor={id}
            title={reason || undefined}
          >
            <input
              id={id}
              type="checkbox"
              checked={selected.has(service)}
              disabled={disabled}
              onChange={(event) => toggle(service, event.target.checked)}
            />
            <span>{label}</span>
            {status ? <small>{status}</small> : null}
          </label>
        );
      })}
      {selected.has('collabra') ? (
        <p className="tree-dashboard-muted">
          Collabra uses Nextcloud storage and may report a manual follow-up status during provisioning.
        </p>
      ) : null}
    </fieldset>
  );
}
